// trajectory.ts — the Viewer's view of a scan's camera path (GET
// /trajectory). Each keyframe resolves to the camera the splats were
// actually fitted against: the bundle-adjusted pose + intrinsics once the
// scan is refined, else the phone's SLAM ones — and carries its loss weight
// and, when that refinement modelled it, its motion blur. weightColor and
// weightTitle render the weight for the scan visualization: a border
// blending white (floor) → green (full), and the exact numbers for the hover
// text; blurTitle adds the modelled exposure path, and cameraTitle joins
// the two.
import type { Intrinsics, Pose, Trajectory, ViewBlur, ViewWeight } from '../api';
import { cameraCenter } from '../splat/camera';
import type { Vec3 } from '../splat/math';

export interface PathCamera {
  index: number; // keyframe index (the JPEG is keyframes/{index}.jpg)
  timestampMs: number;
  pose: Pose;
  intrinsics: Intrinsics;
  refined: boolean;
  center: Vec3;
  weight: ViewWeight | null;
  blur: ViewBlur | null; // modelled motion blur, null when not modelled
}

export function resolvePath(t: Trajectory | null): PathCamera[] {
  if (!t) return [];
  return t.cameras.map((c) => {
    const refined = !!(c.refined_pose && c.refined_intrinsics);
    const pose = refined ? c.refined_pose! : c.pose;
    return {
      index: c.index,
      timestampMs: c.timestamp_ms,
      pose,
      intrinsics: refined ? c.refined_intrinsics! : c.intrinsics,
      refined,
      center: cameraCenter(pose),
      weight: c.weight ?? null,
      blur: c.blur ?? null,
    };
  });
}

// MIN_VIEW_WEIGHT mirrors server/refine's minViewWeight (the floor).
export const MIN_VIEW_WEIGHT = 0.15;
const WHITE: Vec3 = [255, 255, 255];
const GREEN: Vec3 = [34, 197, 94];

// weightColor blends white (weight at the floor) to green (full weight),
// linearly in the weight.
export function weightColor(weight: number): string {
  const t = Math.min(1, Math.max(0, (weight - MIN_VIEW_WEIGHT) / (1 - MIN_VIEW_WEIGHT)));
  const c = WHITE.map((w, i) => Math.round(w + (GREEN[i] - w) * t));
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
}

// weightTitle is the hover text: the exact weight, the tracking factor with
// its measurement, and how much of the frame was deweighted as out of focus.
export function weightTitle(w: ViewWeight): string {
  const track =
    w.n_obs > 0
      ? `${w.reproj_px.toFixed(2)} px reprojection over ${w.n_obs} observations`
      : w.jump_rel > 0
        ? `path jump ${w.jump_rel.toFixed(1)}× the median step`
        : 'not checked';
  const lines = [`Loss weight ${w.weight.toFixed(3)}`, `tracking ×${w.track.toFixed(3)} (${track})`];
  if (w.defocus !== undefined) {
    lines.push(
      w.defocus > 0
        ? `out of focus: ${(100 * w.defocus).toFixed(0)}% of pixels deweighted (mean pixel weight ${(w.focus_weight ?? 1).toFixed(2)})`
        : 'in focus',
    );
  }
  return lines.join('\n');
}

// blurTitle is the hover line for a modelled motion blur: the predicted
// smear, the camera motion over the exposure and the sub-poses it is
// rendered as, plus the rolling-shutter ratio when that was modelled.
export function blurTitle(b: ViewBlur, rollingShutter?: number): string {
  const sub = `${b.samples} sub-pose${b.samples === 1 ? '' : 's'}`;
  const parts = [
    `motion blur ${b.smear_px.toFixed(1)} px (rot ${b.rot_deg.toFixed(1)}°, ${b.trans_mm.toFixed(1)} mm), ${sub}`,
  ];
  if (rollingShutter && rollingShutter > 0) {
    parts.push(`rolling shutter ×${Number(rollingShutter.toFixed(2))}${b.bands > 1 ? ` (${b.bands} row bands)` : ''}`);
  }
  return parts.join(', ');
}

// cameraTitle is a path camera's full hover text: its loss weight and, on
// its own line, its modelled blur; undefined when it carries neither.
export function cameraTitle(c: Pick<PathCamera, 'weight' | 'blur'>, rollingShutter?: number): string | undefined {
  const lines: string[] = [];
  if (c.weight) lines.push(weightTitle(c.weight));
  if (c.blur) lines.push(blurTitle(c.blur, rollingShutter));
  return lines.length ? lines.join('\n') : undefined;
}

// formatExposure renders an exposure time the way a camera does: 1/N s
// below half a second (N rounded), else seconds.
export function formatExposure(s: number): string {
  if (!(s > 0)) return '—';
  if (s >= 0.5) return `${Number(s.toFixed(2))} s`;
  return `1/${Math.round(1 / s)} s`;
}

// formatFocusDistance is a focus plane rho (inverse depth, 1/m) as a
// distance: ∞ at rho ≤ 0 (0 means focused at infinity; the model can push
// rho past it, which is still "beyond everything").
export function formatFocusDistance(rho: number): string {
  if (!(rho > 0)) return '∞';
  const m = 1 / rho;
  return m < 1 ? `${Math.round(m * 1000)} mm` : `${Number(m.toFixed(2))} m`;
}

function formatDistance(m: number): string {
  return m < 1 ? `${Math.round(m * 1000)} mm` : `${Number(m.toFixed(2))} m`;
}

// LensReadoutItem is one value of the Image Degradation readout: a label,
// the value, and the tooltip explaining what it means.
export interface LensReadoutItem {
  key: string;
  label: string;
  value: string;
  help: string;
}

// lensReadout is the Image Degradation readout for a keyframe whose blur
// report carries the lens model's view (aperture_m > 0); [] otherwise.
// Pixel values are at the trainer's resolution, as the report gives them.
export function lensReadout(b: ViewBlur): LensReadoutItem[] {
  if (!(b.aperture_m && b.aperture_m > 0)) return [];
  const rho = b.focus_rho ?? 0;
  const depth = b.depth_m ?? 0;
  const items: LensReadoutItem[] = [
    {
      key: 'exposure',
      label: 'Exposure',
      value: formatExposure(b.exposure_s ?? 0),
      help:
        'The exposure time the lens model learned for this keyframe (smoothed over capture time, pulled towards the ' +
        "camera's reported exposure when it logged one). The motion blur is this time × the camera's velocity.",
    },
    {
      key: 'smear',
      label: 'Motion smear',
      value: `${b.smear_px.toFixed(1)} px · ${b.rot_deg.toFixed(2)}° · ${b.trans_mm.toFixed(1)} mm`,
      help:
        'How far the image moved during the exposure at the scene depth, in training pixels (start to end, rendered ' +
        'as a box smear), then how far the camera turned and travelled over the exposure.',
    },
    {
      key: 'focus',
      label: 'Focus',
      value: `${formatFocusDistance(rho)} vs scene ${depth > 0 ? formatDistance(depth) : '—'}`,
      help:
        'The distance the lens model says this frame was focused at (1/ρ), against the median depth of the scene in ' +
        'view. The further the two are apart, the softer the scene.',
    },
    {
      key: 'defocus',
      label: 'Defocus σ',
      value: `${(b.defocus_px ?? 0).toFixed(2)} px`,
      help:
        `Gaussian σ of the focus blur at the scene depth, in training pixels: α·fx·|1/depth − ρ|, with the scan's ` +
        `aperture coefficient α = ${(b.aperture_m * 1000).toFixed(2)} mm. Splats nearer or farther than the scene ` +
        'depth blur by their own amount.',
    },
  ];
  return items;
}
